/**
 * Seeding a Hub debug form from a guarantee letter [HM-11245].
 *
 * The server sends what Hub Sync Repair would send for the letter, with times and amounts as
 * text: a number input drops a trailing zero (1234.50) and a date picker drops seconds and
 * ticks. Each form field remembers the value it was seeded with. On Execute, a field the engineer
 * left untouched is sent as the server's raw value, so an unedited seeded payload is
 * byte-identical to the operator's; an edited one is sent as edited.
 *
 * `fields` maps each payload field to { fromSeed(raw) => form value, toInput(form value) => what
 * the form would post }. A field is edited when its toInput no longer matches the seeded one.
 */
export default function createLetterSeed(fields) {
  const names = Object.keys(fields);

  const seed = {
    letterNumber: '',
    isFinal: false,
    loading: 0,
    notes: [],
    loadedLetterNumber: null,
    raw: null,
    seeded: null,
    isLoaded,
    isEdited,
    hasEdits,
    reset,
    toInput,
    load,
  };

  function isLoaded() {
    return seed.raw !== null;
  }

  function isEdited(vm, name) {
    return isLoaded() && !angular.equals(fields[name].toInput(vm[name]), seed.seeded[name]);
  }

  function hasEdits(vm) {
    return isLoaded() && names.some((name) => isEdited(vm, name));
  }

  // Copies, so typing in the form never changes the raw seed or the remembered values.
  function apply(vm, payload) {
    seed.raw = angular.copy(payload);
    seed.seeded = {};
    names.forEach((name) => {
      vm[name] = fields[name].fromSeed(angular.copy(payload[name]));
      seed.seeded[name] = angular.copy(fields[name].toInput(vm[name]));
    });
  }

  function reset(vm) {
    apply(vm, seed.raw);
  }

  // The form's own input, with every untouched seeded field put back to the server's raw value.
  function toInput(vm, built) {
    if (!isLoaded()) return built;
    const input = { ...built };
    names.forEach((name) => {
      if (!isEdited(vm, name)) input[name] = seed.raw[name];
    });
    return input;
  }

  // `fetch(input)` calls the seed endpoint. A refusal (no such letter, no live request) is shown
  // by ABP's usual error dialog, as Look up on Hub Sync Repair does, and leaves the form as it was.
  function load(vm, fetch) {
    const letterNumber = (seed.letterNumber || '').trim();
    if (!letterNumber) return;

    const go = () => {
      seed.loading += 1;
      fetch({ letterNumber, isFinal: seed.isFinal })
        .success((result) => {
          apply(vm, result.payload);
          seed.notes = result.notes || [];
          seed.loadedLetterNumber = letterNumber;
        })
        .finally(() => {
          seed.loading -= 1;
        });
    };

    if (hasEdits(vm)) {
      abp.message.confirm(
        App.localize('HubDebugSeedDiscardEditsBody'),
        App.localize('HubDebugSeedDiscardEditsTitle'),
        (confirmed) => {
          if (confirmed) go();
        }
      );
    } else {
      go();
    }
  }

  return seed;
}
